import { TextureSet, EMPTY_TEXTURE_SET } from '../rendering/Texture';
import FontTexture, { SDFCharacter, getSDFFont } from '../rendering/SDFFont';

class TextCharacter {
	fontCharacter: SDFCharacter;
	x: number;
	y: number;
	width: number;
	height: number;

	constructor (fontCharacter: SDFCharacter, x: number, y: number, width: number, height: number) {
		this.fontCharacter = fontCharacter;
		this.x = x;
		this.y = y;
		this.width = width;
		this.height = height;
	}
}

type onUpdate = () => void;

export default class SDFText {
	dirty: boolean;

	private _font: string;
	private _string: string;
	private _size: number;
	private _multiline: boolean;
	private _width: number;
	private _height: number;
	private _fontTexture: FontTexture | null;

	characters: Array<TextCharacter>;
	textureSet: TextureSet;

	private _onTextUpdate: onUpdate;
	private _onLayoutUpdate: onUpdate;

	constructor (onTextUpdate: onUpdate, onFontUpdate: onUpdate) {
		this.dirty = true;

		this._font = 'default';
		this._string = '';
		this._size = 11;
		this._multiline = false;
		this._width = Infinity;
		this._height = Infinity;
		this._fontTexture = null;

		this.characters = [];
		this.textureSet = EMPTY_TEXTURE_SET;

		this._onTextUpdate = onTextUpdate;
		this._onLayoutUpdate = onFontUpdate;
	}

	get string () {
		return this._string;
	}

	set string (string) {
		this.dirty = this.dirty || (this._string !== string);
		if (this.dirty) {
			this._string = string;
			this._onTextUpdate();
		}
	}

	get size () {
		return this._size;
	}

	set size (size) {
		this.dirty = this.dirty || (this._size !== size);
		this._size = size;
	}

	get font () {
		return this._font;
	}

	set font (font) {
		this.dirty = this.dirty || (this._font !== font);
		if (this.dirty) {
			this._font = font;
			this._fontTexture = getSDFFont(this._font, true);
		}
	}

	get multiline () {
		return this._multiline;
	}

	set multiline (multiline) {
		this.dirty = this.dirty || (this._multiline !== multiline);
		this._multiline = multiline;
	}

	get width () {
		return this._width;
	}

	set width (width) {
		this.dirty = this.dirty || (this._width !== width);
		this._width = width;
	}

	get height () {
		return this._height;
	}

	set height (height) {
		this.dirty = this.dirty || (this._height !== height);
		this._height = height;
	}

	computeLayout () {
		const fontTexture = this._fontTexture;
		if (fontTexture === null || this.dirty === false) {
			return;
		}

		if (this._string === '' || this._size === 0) {
			this.characters.length = 0;
			return;
		}

		const sdfTexture = fontTexture.updateWithCharacters(this._string, this._size);
		this.textureSet = sdfTexture.textureSet;

		this.characters.length = this._string.length;

		// Computing character dimensions
		const sdfCharacters = sdfTexture.characters;
		const lineHeight = this._size * 1.2; // Line spacing multiplier
		let quadX = 0;
		let lineY = 0; // Vertical offset for current line
		
		for (let c = 0; c < this._string.length; c++) {
			const character = this._string[c];
			
			// Handle newline characters
			if (character === '\n') {
				quadX = 0;
				lineY -= lineHeight;
				// Create a zero-size character for the newline
				const fontCharacter = sdfCharacters[' '] || Object.values(sdfCharacters)[0];
				this.characters[c] = new TextCharacter(fontCharacter, 0, lineY, 0, 0);
				continue;
			}
			
			const fontCharacter = sdfCharacters[character];
			if (!fontCharacter) {
				// Skip characters not in the font
				this.characters[c] = new TextCharacter(sdfCharacters[' '] || Object.values(sdfCharacters)[0], quadX, lineY, 0, 0);
				continue;
			}
			
			const charDim = fontCharacter.dimensions;
			const charSpace = fontCharacter.space;

			const textureDim = fontCharacter.textureBounds;
			const textureWidth = textureDim.width;
			const textureHeight = textureDim.height;

			const scale = this._size / fontCharacter.size;
			const quadWidth = 2 * textureWidth * scale;
			const quadHeight = 2 * textureHeight * scale;
			const quadY = charDim.top * scale + lineY;

			this.characters[c] = new TextCharacter(fontCharacter, quadX + charDim.left * scale, quadY, quadWidth, quadHeight);
			quadX += charSpace * scale;
		}

		this._onLayoutUpdate();

		this.dirty = false;
	}

}
